import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { QuestionToolTip } from '@/components/ui/question-tooltip'
import { cn } from '@/lib/utils'
import { PromotionFormData } from './promotion-form'

export const PromotionCombinationCard = () => {
    const { t } = useTranslation('stores')
    const { watch, setValue: rawSetValue } = useFormContext<PromotionFormData>()
    const setValue: typeof rawSetValue = (name, value, options) =>
        rawSetValue(name, value, { shouldDirty: true, ...options })

    const forceCombine = watch('forceCombine')
    const combinesWithOrderLevelDiscounts = watch('combinesWithOrderLevelDiscounts')
    const combinesWithLineItemDiscounts = watch('combinesWithLineItemDiscounts')
    const combinesWithShippingDiscounts = watch('combinesWithShippingDiscounts')

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-1.5">
                    <CardTitle>{t('promotion.settings.combinationSettings')}</CardTitle>
                    <QuestionToolTip title={t('promotion.combinationHelp.title')}>
                        <div className="space-y-4">
                            <p className="text-muted-foreground">{t('promotion.combinationHelp.intro')}</p>
                            <div className="space-y-3">
                                <div>
                                    <p className="font-medium">{t('promotion.fields.forceCombine')}</p>
                                    <p className="text-muted-foreground text-xs">
                                        {t('promotion.combinationHelp.forceCombine')}
                                    </p>
                                </div>
                                <div>
                                    <p className="font-medium">{t('promotion.fields.combinesWithOrderDiscounts')}</p>
                                    <p className="text-muted-foreground text-xs">
                                        {t('promotion.combinationHelp.orderLevel')}
                                    </p>
                                </div>
                                <div>
                                    <p className="font-medium">{t('promotion.fields.combinesWithLineItemDiscounts')}</p>
                                    <p className="text-muted-foreground text-xs">
                                        {t('promotion.combinationHelp.lineItem')}
                                    </p>
                                </div>
                                <div>
                                    <p className="font-medium">{t('promotion.fields.combinesWithShippingDiscounts')}</p>
                                    <p className="text-muted-foreground text-xs">
                                        {t('promotion.combinationHelp.shipping')}
                                    </p>
                                </div>
                            </div>
                            <p className="text-foreground bg-muted rounded-md p-2 text-xs">
                                {t('promotion.combinationHelp.combineRule')}
                            </p>
                            <p className="text-muted-foreground text-xs italic">
                                {t('promotion.combinationHelp.footnote')}
                            </p>
                        </div>
                    </QuestionToolTip>
                </div>
                <p className="text-muted-foreground mt-1 text-sm">
                    {t('promotion.settings.combinationDescription')}
                </p>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="forceCombine"
                        checked={forceCombine || false}
                        onCheckedChange={(checked) => {
                            const next = checked === true
                            setValue('forceCombine', next)
                            if (next) {
                                setValue('combinesWithOrderLevelDiscounts', false)
                                setValue('combinesWithLineItemDiscounts', false)
                                setValue('combinesWithShippingDiscounts', false)
                            }
                        }}
                    />
                    <Label htmlFor="forceCombine" className="text-xs">
                        {t('promotion.fields.forceCombine')}
                    </Label>
                </div>
                <div className={cn('space-y-3', forceCombine && 'pointer-events-none opacity-50')}>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="combinesWithOrderLevelDiscounts"
                            checked={combinesWithOrderLevelDiscounts || false}
                            disabled={forceCombine}
                            onCheckedChange={(checked) => setValue('combinesWithOrderLevelDiscounts', checked === true)}
                        />
                        <Label htmlFor="combinesWithOrderLevelDiscounts" className="text-xs">
                            {t('promotion.fields.combinesWithOrderDiscounts')}
                        </Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="combinesWithLineItemDiscounts"
                            checked={combinesWithLineItemDiscounts || false}
                            disabled={forceCombine}
                            onCheckedChange={(checked) => setValue('combinesWithLineItemDiscounts', checked === true)}
                        />
                        <Label htmlFor="combinesWithLineItemDiscounts" className="text-xs">
                            {t('promotion.fields.combinesWithLineItemDiscounts')}
                        </Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="combinesWithShippingDiscounts"
                            checked={combinesWithShippingDiscounts || false}
                            disabled={forceCombine}
                            onCheckedChange={(checked) => setValue('combinesWithShippingDiscounts', checked === true)}
                        />
                        <Label htmlFor="combinesWithShippingDiscounts" className="text-xs">
                            {t('promotion.fields.combinesWithShippingDiscounts')}
                        </Label>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}
